import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { useAppSelector, useAppDispatch } from '../../Reducers/store';
import { fetchUsers } from '../../Actions/CommonFields';
import UserApi from '../../Api/UserApi';
import { Button, Select, TextInput } from 'flowbite-react';
import { UserClockIn } from '../../Models/UserClockIn';
import { formatDateInputs } from '../../Utils';
import { BasicSearchRequest } from '../../Models/BasicSearchRequest';
import { User } from '../../Models/User';
import TimesheetAdminTable from './TimesheetAdminTable';

export default function TimesheetAdmin() {
  const dispatch = useAppDispatch();
  const { userModel } = useAppSelector((state) => state.user);
  const { users } = useAppSelector((state) => state.commonFields);

  const [clockInListData, setClockInListData] = useState<UserClockIn[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [formData, setFormData] = useState<BasicSearchRequest>({
    status: 0,
    fromDate: new Date(new Date().setDate(new Date().getDate() - 7))
      .toISOString()
      .split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
  });

  const [selectedUserId, setSelectedUserId] = useState<number>(0);

  useEffect(() => {
    dispatch(fetchUsers());
    getClockInList();
  }, [dispatch]);

  const getClockInList = () => {
    if (userModel) {
      try {
        setIsLoading(true);
        return UserApi.getUserClockInList(formData, selectedUserId > 0 ? selectedUserId : undefined)
          .then((res) => {
            setClockInListData(res.data);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error(err);
            toast.error('Error loading timesheet data');
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error('Error loading timesheet data');
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(Number(e.target.value));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    getClockInList();
  };

  const refreshData = () => {
    getClockInList();
  };

  return (
    <>
      <div className='w-full'>
        <div className='w-full h-[74px] p-4'>
          <div className='mx-1 flex items-end'>
            <div className='flex flex-1'>
              <h1 className='text-gray-900 font-bold text-4xl'>
                <div>Timesheet Admin</div>
              </h1>
            </div>
          </div>
        </div>
        <div className='flex'>
          <div className=''>
            <form
              id='searchForm'
              onSubmit={onSubmit}
              className='flex gap-4 justify-center'
            >
              <div className='flex gap-1'>
                <div className='content-center text-xl font-bold'>User:</div>
                <Select
                  id='userId'
                  name='userId'
                  onChange={handleUserChange}
                  value={selectedUserId ?? ''}
                  sizing='md'
                >
                  <option key={0} value={0}>
                    All Users
                  </option>
                  {users && users
                    .filter((x) => x.userLevelId === 3) // Filter for drivers only
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                </Select>
              </div>
              <div className='flex gap-1'>
                <div className='content-center text-xl font-bold'>From:</div>
                <TextInput
                  id='fromDate'
                  name='fromDate'
                  type='date'
                  placeholder='Date...'
                  onChange={handleChange}
                  value={formatDateInputs(formData.fromDate ?? '')}
                  sizing='md'
                  pattern='\d{4}-\d{2}-\d{2}'
                  required
                />
              </div>
              <div className='flex gap-1'>
                <div className='content-center text-xl font-bold'>To:</div>
                <TextInput
                  id='toDate'
                  name='toDate'
                  type='date'
                  placeholder='Date...'
                  onChange={handleChange}
                  value={formatDateInputs(formData.toDate ?? '')}
                  sizing='md'
                  pattern='\d{4}-\d{2}-\d{2}'
                  required
                />
              </div>
              <Button
                type='submit'
                form='searchForm'
                className='buttonMain'
                disabled={buttonDisabled}
              >
                Search
              </Button>
            </form>
          </div>
        </div>
        <div className='w-full p-4 h-[calc(100vh-132px)] md:h-[calc(100vh-200px)] overflow-y-auto'>
          {isLoading && <Loader />}
          <TimesheetAdminTable
            data={clockInListData}
            users={users}
            refreshData={refreshData}
          />
        </div>
      </div>
    </>
  );
}